import { useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui';
import Pumpjack from './ui/Pumpjack';

// Velocity-based ETA. Up to 3 statements process at once, so we don't sum
// per-job estimates — instead we watch how fast the single aggregate percent
// climbs and extrapolate. An exponential moving average keeps the number from
// jumping around. Returns null until we have enough movement to estimate.
function useSmoothedEta(percent: number, active: boolean): number | null {
  const [eta, setEta] = useState<number | null>(null);
  const lastRef = useRef<{ t: number; p: number } | null>(null);
  const rateRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      lastRef.current = null;
      rateRef.current = null;
      setEta(null);
      return;
    }
    const now = Date.now();
    if (lastRef.current === null) {
      lastRef.current = { t: now, p: percent };
      return;
    }
    const last = lastRef.current;
    const dt = (now - last.t) / 1000;
    if (dt < 0.5) return;
    const dp = percent - last.p;
    lastRef.current = { t: now, p: percent };
    if (dp <= 0) return; // no forward progress this tick — keep the last estimate
    const inst = dp / dt; // percent per second
    rateRef.current =
      rateRef.current === null ? inst : 0.35 * inst + 0.65 * rateRef.current;
    const rate = rateRef.current;
    if (rate && rate > 0) {
      setEta(Math.max(0, (100 - percent) / rate));
    }
  }, [percent, active]);

  return eta;
}

function formatEta(sec: number | null): string {
  if (sec === null) return 'Estimating time remaining\u2026';
  if (sec <= 6) return 'Almost done\u2026';
  if (sec < 60) return 'Less than a minute remaining';
  const mins = Math.round(sec / 60);
  if (mins < 60) return `About ${mins} minute${mins === 1 ? '' : 's'} remaining`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (rem === 0) return `About ${hrs} hour${hrs === 1 ? '' : 's'} remaining`;
  return `About ${hrs} hr ${rem} min remaining`;
}

export interface ExtractionProgressProps {
  /** 0-100 weighted overall progress across the batch. */
  percent: number;
  /** True while statements are queued / uploading / being read. */
  active: boolean;
  total: number;
  done: number;
  attention: number;
  showDetails: boolean;
  onToggleDetails: () => void;
}

export default function ExtractionProgress({
  percent,
  active,
  total,
  done,
  attention,
  showDetails,
  onToggleDetails,
}: ExtractionProgressProps) {
  const eta = useSmoothedEta(percent, active);
  const shown = Math.min(100, Math.max(0, Math.round(percent)));

  let title: string;
  let TitleIcon: typeof CheckCircle | null = null;
  let titleIconClass = '';
  if (active) {
    title = 'Extracting your statements';
  } else if (attention > 0 && done > 0) {
    title = 'Statements imported';
    TitleIcon = AlertTriangle;
    titleIconClass = 'text-clay-500';
  } else if (attention > 0) {
    title = 'Needs your attention';
    TitleIcon = AlertTriangle;
    titleIconClass = 'text-clay-500';
  } else {
    title = 'Extraction complete';
    TitleIcon = CheckCircle;
    titleIconClass = 'text-green-600';
  }

  let statusLine: string;
  if (active) {
    statusLine = formatEta(eta);
  } else if (attention > 0) {
    statusLine = `${done} of ${total} imported \u00b7 ${attention} need${attention === 1 ? 's' : ''} your attention`;
  } else {
    statusLine = `All ${total} statement${total === 1 ? '' : 's'} imported`;
  }

  const subLine = active
    ? `${done} of ${total} statement${total === 1 ? '' : 's'} done`
    : null;

  return (
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          <Pumpjack animate={active} size={88} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="t-card text-green-900 flex items-center gap-2">
            {TitleIcon && <TitleIcon size={16} className={titleIconClass} />}
            {title}
          </h3>
          <p className="text-sm text-green-700 mt-1 font-medium">{statusLine}</p>
          {subLine && <p className="text-xs text-ink-faint mt-1">{subLine}</p>}
        </div>

        <div className="text-right flex-shrink-0">
          <p className="t-num-hero text-green-900">{shown}%</p>
          <p className="text-xs text-ink-faint mt-1">complete</p>
        </div>
      </div>

      {/* Single aggregate progress bar */}
      <div className="mt-4 h-2 rounded-full bg-cream-dark overflow-hidden">
        <div
          className="h-full bg-green-600 transition-[width] duration-700 ease-out rounded-full"
          style={{ width: `${Math.max(shown, active ? 4 : shown)}%` }}
        />
      </div>

      {/* Optional detail toggle */}
      <div className="mt-3 flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          icon={showDetails ? ChevronUp : ChevronDown}
          onClick={onToggleDetails}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </Button>
      </div>
    </div>
  );
}
